import React, { useState, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { CustomerLogo, OverrideCountChip } from "../../components/primitives";
import {
  getAddressSummary,
  isInvoiceDeliveryReady,
  mergeOrganizationOptions,
} from "../../model/organizationModel";
import { getTotalOrganizationOverrideCount } from "../../model/overrideModel";
import { compactInputSx, ui } from "../../styles";

const GROUP_LABEL_IDS = {
  knownCompanies: "admin.crm.customerCard.organizationLink.knownOrganizations",
  publicCompanies: "admin.crm.customerCard.organizationLink.allOrganizations",
  supplierCompanies: "admin.crm.customerCard.organizationLink.supplierOrganizations",
};

const getOrganizationGroupLabelId = option =>
  GROUP_LABEL_IDS[option?.source] || "admin.crm.customerCard.organizationLink.allOrganizations";

const searchOrganization = (option, query, locale) => {
  const searchableFields = [
    option?.name,
    option?.orgNo,
    option?.vatRegNo,
    option?.publicCompanyId,    
    getAddressSummary(option?.invoiceAddress),
    getAddressSummary(option?.deliveryAddress),
  ];

  return searchableFields
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase(locale)
    .includes(query);
};

const OrganizationLinkEditor = ({
  availableOrganizations,
  onClose,
  onOpenOrganization,
  onOrganizationChange,
  selectedOrganization,
}) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const locale = intl.locale || undefined;

  const handleChange = (_event, value) => {
    onOrganizationChange(value);
    if (value) onClose();
  };

  const handleFilterOptions = (options, state) => {
    const query = state.inputValue.trim().toLocaleLowerCase(locale);
    if (!query) return options;
    return options.filter(option => searchOrganization(option, query, locale));
  };

  const handleRemove = () => {
    onOrganizationChange(null);
    onClose();
  };

  return (
    <Box sx={{ width: { xs: 330, sm: 430 }, maxWidth: "calc(100vw - 24px)" }}>
      <Box sx={{ px: 1.15, pt: 1.05, pb: 0.8, borderBottom: `1px solid ${ui.border}` }}>
        <Stack direction="row" spacing={0.8} alignItems="flex-start">
          <Box
            sx={{
              width: 30,
              height: 30,
              display: "grid",
              placeItems: "center",
              borderRadius: 1,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <LinkRoundedIcon sx={{ fontSize: 15 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: ui.text, fontSize: 11.3, lineHeight: 1.25, fontWeight: 650 }}>
              {t(
                selectedOrganization
                  ? "admin.crm.customerCard.organizationLink.changeTitle"
                  : "admin.crm.customerCard.organizationLink.addTitle"
              )}
            </Typography>
            <Typography sx={{ mt: 0.15, color: ui.muted, fontSize: 9.3, lineHeight: 1.45 }}>
              {t("admin.crm.customerCard.organizationLink.description")}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("admin.crm.customerCard.actions.close")}
            sx={{ mt: -0.25, mr: -0.25, width: 26, height: 26, color: ui.muted }}
          >
            <CloseRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 1.05 }}>
        <Autocomplete
          fullWidth
          disablePortal
          options={availableOrganizations}
          value={selectedOrganization}
          openOnFocus
          autoHighlight
          clearOnBlur={false}
          onChange={handleChange}
          getOptionLabel={option => option?.name || ""}
          isOptionEqualToValue={(option, value) => option?.companyId === value?.companyId}
          groupBy={option => t(getOrganizationGroupLabelId(option))}
          filterOptions={handleFilterOptions}
          ListboxProps={{ style: { maxHeight: 260, paddingTop: 0, paddingBottom: 0 } }}
          renderGroup={params => (
            <li key={params.key}>
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  px: 1.25,
                  py: 0.5,
                  bgcolor: "#f8fafc",
                  borderTop: `1px solid ${ui.border}`,
                  borderBottom: `1px solid ${ui.border}`,
                }}
              >
                <Typography
                  sx={{
                    color: ui.muted,
                    fontSize: 9.1,
                    fontWeight: 650,
                    textTransform: "uppercase",
                    letterSpacing: ".045em",
                  }}
                >
                  {params.group}
                </Typography>
              </Box>
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option) => {
            const subtitle =
              [option?.orgNo, getAddressSummary(option?.invoiceAddress)]
                .filter(Boolean)
                .join(" · ") || `ID ${option?.publicCompanyId}`;

            return (
              <Box
                component="li"
                {...props}
                sx={{
                  ...props.sx,
                  px: "10px !important",
                  py: "7px !important",
                  alignItems: "center !important",
                  borderBottom: `1px solid ${ui.border}`,
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <CustomerLogo size={30} fallbackIconSize={15} />
                <Box sx={{ ml: 0.8, minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography noWrap sx={{ color: ui.text, fontSize: 10.5, fontWeight: 600 }}>
                      {option?.name}
                    </Typography>
                  </Stack>
                  <Typography noWrap sx={{ mt: 0.06, color: ui.muted, fontSize: 9.2 }}>
                    {subtitle}
                  </Typography>
                </Box>
                {isInvoiceDeliveryReady(option) && (
                  <Tooltip title={t("admin.crm.customerCard.organizationLink.invoiceReadyTooltip")}>
                    <ReceiptLongOutlinedIcon sx={{ ml: 0.7, color: "#248a52", fontSize: 15, flexShrink: 0 }} />
                  </Tooltip>
                )}
              </Box>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("admin.crm.customerCard.organizationLink.searchLabel")}
              placeholder={t("admin.crm.customerCard.organizationLink.searchPlaceholder")}
              sx={{
                ...compactInputSx,
                position: "relative",
                "& .MuiAutocomplete-inputRoot": { minHeight: "32px !important", py: "0 !important" },
                "& .MuiAutocomplete-input": { height: "32px !important", py: "0 !important" },
                "& .MuiAutocomplete-popperDisablePortal": { zIndex: 3 },
              }}
            />
          )}
        />

        <Typography sx={{ mt: 0.55, color: ui.subtle, fontSize: 8.9, lineHeight: 1.4 }}>
          {t("admin.crm.customerCard.organizationLink.searchHelp")}
        </Typography>

        {selectedOrganization && (
          <Stack
            direction="row"
            spacing={0.65}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0.85, pt: 0.8, borderTop: `1px solid ${ui.border}` }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ color: ui.text, fontSize: 9.7, fontWeight: 600 }}>
                {t("admin.crm.customerCard.organizationLink.current", { name: selectedOrganization.name })}
              </Typography>
              <Typography sx={{ mt: 0.05, color: ui.subtle, fontSize: 8.8 }}>
                {t("admin.crm.customerCard.organizationLink.unlinkKeepsData")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.3} flexShrink={0}>
              {onOpenOrganization && (
                <Button
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewRoundedIcon sx={{ fontSize: "12px !important" }} />}
                  onClick={() => onOpenOrganization(selectedOrganization)}
                  sx={{ minHeight: 26, px: 0.45, color: ui.muted, textTransform: "none", fontSize: 8.9 }}
                >
                  {t("admin.crm.customerCard.actions.view")}
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                onClick={handleRemove}
                sx={{ minHeight: 26, px: 0.45, color: ui.danger, textTransform: "none", fontSize: 8.9, fontWeight: 600 }}
              >
                {t("admin.crm.customerCard.organizationLink.remove")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const OrganizationLinkSection = ({
  availableOrganizations,
  baseline,
  draft,
  onOpenOrganization,
  onOrganizationChange,
  selectedOrganization,
}) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const [anchorEl, setAnchorEl] = useState(null);

  const overrideCount = selectedOrganization ? getTotalOrganizationOverrideCount(draft, baseline) : 0;

  const flattenedOrganizations = useMemo(
    () => mergeOrganizationOptions(availableOrganizations),
    [availableOrganizations]
  );

  const close = () => setAnchorEl(null);
  const open = event => setAnchorEl(event.currentTarget);

  return (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} alignItems={{ xs: "stretch", sm: "center" }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            display: "grid",
            placeItems: "center",
            borderRadius: 1,
            bgcolor: ui.soft,
            color: ui.muted,
            flexShrink: 0,
          }}
        >
          <LinkRoundedIcon sx={{ fontSize: 15 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: ui.text, fontSize: 10.6, fontWeight: 600 }}>
            {t("admin.crm.customerCard.organizationLink.sectionTitle")}
          </Typography>

          {selectedOrganization ? (
            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mt: 0.12, flexWrap: "wrap", rowGap: 0.25 }}>
              <CustomerLogo size={20} fallbackIconSize={11} />
              <Typography noWrap sx={{ maxWidth: 260, color: ui.muted, fontSize: 9.5, fontWeight: 550 }}>
                {selectedOrganization.name}
              </Typography>
              <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#32a46d", flexShrink: 0 }} />
              <Typography sx={{ color: "#247a4d", fontSize: 9, fontWeight: 600 }}>
                {t("admin.crm.customerCard.organizationLink.linked")}
              </Typography>
              <OverrideCountChip
                count={overrideCount}
                label={t("admin.crm.customerCard.organizationLink.changedFields", { count: overrideCount })}
              />
            </Stack>
          ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.08 }}>
                <Box
                  component="span"
                  sx={{ width: 5, height: 5, borderRadius: "50%", border: `1px solid ${ui.subtle}`, flexShrink: 0 }}
                />
                <Typography sx={{ color: ui.subtle, fontSize: 9.3 }}>
                  {t("admin.crm.customerCard.organizationLink.notLinked")}
                </Typography>
              </Stack>
            )}
        </Box>

        <Button
          size="small"
          variant="text"
          startIcon={selectedOrganization ? <EditOutlinedIcon sx={{ fontSize: 14 }} /> : <AddRoundedIcon sx={{ fontSize: 15 }} />}
          onClick={open}
          sx={{ minHeight: 28, px: 0.65, color: "primary.main", textTransform: "none", fontSize: 9.8, fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {t(selectedOrganization ? "admin.crm.customerCard.actions.change" : "admin.crm.customerCard.organizationLink.add")}
        </Button>
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.65,
              borderRadius: 1.5,
              border: `1px solid ${ui.border}`,
              boxShadow: "0 12px 32px rgba(16,24,40,.14), 0 2px 8px rgba(16,24,40,.06)",
              overflow: "visible",
              isolation: "isolate",
            },
          },
        }}
      >
        <OrganizationLinkEditor
          availableOrganizations={flattenedOrganizations}
          onClose={close}
          onOpenOrganization={onOpenOrganization}
          onOrganizationChange={onOrganizationChange}
          selectedOrganization={selectedOrganization}
        />
      </Popover>
    </>
  );
};

export default OrganizationLinkSection;